import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isWithinInterval, parseISO } from 'date-fns';
import { useFinanceProcurementsByStatus } from '@/hooks/use-finance-procurements-by-status';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarIcon, ChevronLeft, ChevronRight, InfoIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Procurement } from '@/types/database.types';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';

const FinancePaymentCalendar: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  const {
    procurements: allProcurements,
    isLoading
  } = useFinanceProcurementsByStatus({ 
    status: ['price_confirmed', 'quality_confirmed', 'agreement_signed', 'pickup_scheduled', 'harvested', 'in_transit', 'delivered']
  });
  
  // Filter only procurements with payment_due_date
  const procurementsWithDueDate = useMemo(() => {
    return allProcurements.filter(p => p.finance?.payment_due_date);
  }, [allProcurements]);
  
  // Calculate payment date ranges for the calendar
  const calendarMonth = useMemo(() => {
    if (!selectedMonth) return null;
    
    const start = startOfMonth(selectedMonth);
    const end = endOfMonth(selectedMonth);
    
    return {
      start,
      end,
      days: eachDayOfInterval({ start, end })
    };
  }, [selectedMonth]);
  
  // Group payments by date for the selected month
  const paymentsByDate = useMemo(() => {
    if (!calendarMonth || !procurementsWithDueDate.length) return {};
    
    const dateMap: Record<string, Procurement[]> = {};
    
    // Initialize all days in the month with empty arrays
    calendarMonth.days.forEach(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      dateMap[dateKey] = [];
    });
    
    // Add procurements to their respective due dates
    procurementsWithDueDate.forEach(procurement => {
      if (!procurement.finance?.payment_due_date) return;
      
      const dueDate = parseISO(procurement.finance.payment_due_date.toString());
      
      // Only include payments within the selected month
      if (isWithinInterval(dueDate, { 
        start: calendarMonth.start,
        end: calendarMonth.end
      })) {
        const dateKey = format(dueDate, 'yyyy-MM-dd');
        if (!dateMap[dateKey]) dateMap[dateKey] = [];
        dateMap[dateKey].push(procurement);
      }
    });
    
    return dateMap;
  }, [calendarMonth, procurementsWithDueDate]);
  
  // Calculate total payment amount for each day
  const paymentAmountsByDate = useMemo(() => {
    const amountMap: Record<string, number> = {};
    
    Object.entries(paymentsByDate).forEach(([date, procurements]) => {
      amountMap[date] = procurements.reduce((sum, p) => sum + (p.total_amount || 0), 0);
    });
    
    return amountMap;
  }, [paymentsByDate]);
  
  // Get payments for the selected date
  const selectedDatePayments = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    return paymentsByDate[dateKey] || [];
  }, [selectedDate, paymentsByDate]);
  
  // Calculate total payment amount for the selected date
  const selectedDateTotal = useMemo(() => {
    return selectedDatePayments.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  }, [selectedDatePayments]);
  
  const previousMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() - 1);
      return newMonth;
    });
  };
  
  const nextMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + 1);
      return newMonth;
    });
  };
  
  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Payment Calendar</h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={previousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-medium min-w-[120px] text-center">
              {format(selectedMonth, 'MMMM yyyy')}
            </div>
            <Button variant="outline" size="icon" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <Calendar
          mode="single"
          selected={selectedDate || undefined}
          onSelect={date => handleDayClick(date as Date)}
          month={selectedMonth}
          className="rounded-md border"
          classNames={{
            day: "p-0 relative [&:has([aria-selected])]:bg-transparent focus-within:relative focus-within:z-20",
            day_selected: "bg-transparent text-foreground hover:bg-muted hover:text-foreground",
          }}
          components={{
            Day: ({ date, ...props }) => {
              const dateKey = format(date, 'yyyy-MM-dd');
              const hasPayments = paymentsByDate[dateKey]?.length > 0;
              const paymentAmount = paymentAmountsByDate[dateKey] || 0;
              
              return (
                <div
                  {...props}
                  className={`h-14 w-14 p-0 font-normal aria-selected:opacity-100 hover:bg-muted relative flex flex-col items-center justify-start ${
                    hasPayments ? 'bg-blue-50' : ''
                  } ${
                    selectedDate && isSameDay(date, selectedDate) ? 'border-2 border-blue-500' : ''
                  }`}
                >
                  <span className="absolute top-1 right-1 text-xs">{format(date, 'd')}</span>
                  {hasPayments && (
                    <>
                      <div className="absolute bottom-1 w-full px-1">
                        <div className="bg-blue-100 text-blue-800 text-xs p-0.5 rounded text-center">
                          {paymentsByDate[dateKey].length} payment{paymentsByDate[dateKey].length > 1 ? 's' : ''}
                        </div>
                        <div className="text-[9px] font-medium text-center text-blue-800">
                          Br {paymentAmount.toFixed(0)}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            }
          }}
        />
      </div>

      <div>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>
                {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'No Date Selected'}
              </CardTitle>
              {selectedDate && (
                <div className="bg-blue-100 px-2 py-1 rounded text-sm font-medium text-blue-800">
                  Br {selectedDateTotal.toFixed(2)}
                </div>
              )}
            </div>
            <CardDescription>
              {selectedDatePayments.length 
                ? `${selectedDatePayments.length} payment${selectedDatePayments.length > 1 ? 's' : ''} due` 
                : 'No payments due'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <div className="flex flex-col items-center justify-center text-center py-8 text-muted-foreground space-y-2">
                <CalendarIcon className="h-8 w-8" />
                <p>Select a date to view scheduled payments</p>
              </div>
            ) : selectedDatePayments.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-8 text-muted-foreground space-y-2">
                <InfoIcon className="h-8 w-8" />
                <p>No payments scheduled for this date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDatePayments.map(procurement => (
                  <div key={procurement.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium">{procurement.product_name || 'Untitled Procurement'}</h4>
                        <Badge variant="outline" className="mt-1">{procurement.source_type}</Badge>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">View</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Payment Details</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-3">
                            <div className="grid grid-cols-2 gap-y-2">
                              <div className="text-sm font-medium">ID</div>
                              <div className="text-sm font-mono">{procurement.id.substring(0, 12)}...</div>
                              
                              <div className="text-sm font-medium">Product</div>
                              <div className="text-sm">{procurement.product_name || 'N/A'}</div>
                              
                              <div className="text-sm font-medium">Source</div>
                              <div className="text-sm capitalize">{procurement.source_type}</div>
                              
                              <div className="text-sm font-medium">Status</div>
                              <div className="text-sm capitalize">{procurement.status.replace(/_/g, ' ')}</div>
                              
                              <div className="text-sm font-medium">Payment Term</div>
                              <div className="text-sm capitalize">{procurement.finance?.payment_term_type}</div>
                              
                              <div className="text-sm font-medium">Amount</div>
                              <div className="text-sm font-semibold">Br {procurement.total_amount?.toFixed(2)}</div>
                            </div>
                            
                            <div className="pt-2 border-t">
                              <DialogClose asChild>
                                <div className="flex justify-end gap-3">
                                  <Button variant="outline" asChild>
                                    <Link to={`/procurements/${procurement.id}`}>
                                      Full Details
                                    </Link>
                                  </Button>
                                </div>
                              </DialogClose>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-semibold">Br {procurement.total_amount?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-muted-foreground">Status:</span>
                        <span className="capitalize">{procurement.status.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinancePaymentCalendar;
